import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatBatchLabel } from '../lib/batch';
import { formatQty } from '../lib/quantity';
import { honeyroomApi, type HoneyroomDevice, type HoneyroomOverview } from '../services/honeyroom';

const FONT_FAMILY = '"Segoe UI", Arial, sans-serif';
const CARD_ACTIVE = 'linear-gradient(180deg, #79876f 0%, #64725a 100%)';
const CARD_IDLE = 'linear-gradient(180deg, #707b8d 0%, #586172 100%)';
const CARD_BORDER = 'rgba(255,255,255,0.18)';
const METRIC_BG = 'rgba(0,0,0,0.10)';
const METRIC_BORDER = 'rgba(255,255,255,0.22)';

function deviceIsPowered(device: HoneyroomDevice): boolean {
  return (device.metrics.powerW ?? 0) > 20 || device.metrics.heaterOn === true || device.metrics.mixerOn === true;
}

function metricValues(device: HoneyroomDevice): string[] {
  if (device.shape === 'cabinet') {
    return [
      device.metrics.humidityPct !== null ? `${Math.round(device.metrics.humidityPct)}%` : '–',
      device.metrics.temperatureC !== null ? `${device.metrics.temperatureC.toFixed(1)}°` : '–',
      device.metrics.powerW !== null ? `${Math.round(device.metrics.powerW)}w` : '–',
    ];
  }

  return [
    `${formatQty(device.totalKg, 'kg')}kg`,
    device.metrics.temperatureC !== null ? `${device.metrics.temperatureC.toFixed(1)}°` : '–',
    device.metrics.powerW !== null ? `${Math.round(device.metrics.powerW)}w` : '–',
  ];
}

function MachineVisual({ device }: { device: HoneyroomDevice }) {
  const fill = device.totalKg > 0 ? Math.max(18, Math.min(72, device.totalKg * 0.45)) : 0;
  const mixerOn = device.metrics.mixerOn === true;
  const isCabinet = device.shape === 'cabinet';
  const scale = isCabinet ? 1.12 : device.shape === 'creamer' ? 0.88 : 1;

  return (
    <div className="relative flex h-full items-center justify-center">
      <div
        className="relative"
        style={{
          width: `${112 * scale}px`,
          height: `${132 * scale}px`,
        }}
      >
        {isCabinet ? (
          <>
            <div
              className="absolute inset-0 rounded-[18px] bg-[#f8fafc]"
              style={{ border: '3px solid #354052' }}
            />
            <div
              className="absolute left-[14%] top-[14%] h-[72%] w-[62%] rounded-[12px] bg-[#f6b96b]"
              style={{ border: '3px solid #e7edf5' }}
            />
            <div className="absolute right-[10px] top-[44%] h-[26px] w-[4px] rounded-full bg-[#354052]" />
          </>
        ) : (
          <>
            <div
              className="absolute left-[18%] top-[4%] h-[54%] w-[48%] rounded-t-[4px] bg-[#fbfdff]"
              style={{ border: '3px solid #354052', borderBottom: 'none' }}
            />
            <div
              className="absolute left-[18%] top-[4%] h-[72%] w-[48%]"
              style={{
                clipPath: 'polygon(0 0, 100% 0, 100% 57%, 68% 83%, 60% 100%, 40% 100%, 32% 83%, 0 57%)',
                backgroundColor: '#fbfdff',
                border: '3px solid #354052',
              }}
            />
            {fill > 0 ? (
              <div
                className="absolute left-[18%] w-[48%] bg-[#de9648]"
                style={{
                  bottom: '30%',
                  height: `${fill}px`,
                  clipPath: 'polygon(0 0, 100% 0, 100% 48%, 68% 100%, 32% 100%, 0 48%)',
                }}
              />
            ) : null}
            <div
              className="absolute left-1/2 bottom-[10%] h-[12%] w-[12%] -translate-x-1/2 bg-[#fbfdff]"
              style={{ border: '3px solid #354052' }}
            />
            <div className="absolute bottom-[6%] left-[31%] h-[18%] w-[3px] rounded-full bg-[#354052]" />
            <div className="absolute bottom-[6%] right-[31%] h-[18%] w-[3px] rounded-full bg-[#354052]" />
          </>
        )}

        {mixerOn ? (
          <div className="absolute right-[-10px] top-[42%] flex h-8 w-8 items-center justify-center rounded-full bg-white/90">
            <div className="h-5 w-5 animate-spin rounded-full border-[3px] border-[#7dcab4] border-t-transparent" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function MetricRow({ device }: { device: HoneyroomDevice }) {
  const values = metricValues(device);

  return (
    <div className="grid h-10 grid-cols-3 gap-2">
      {values.map((value, index) => (
        <div
          key={`${device.id}-${index}`}
          className="flex items-center justify-center rounded-xl text-white"
          style={{
            height: '40px',
            border: `1px solid ${METRIC_BORDER}`,
            background: METRIC_BG,
            fontFamily: FONT_FAMILY,
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {value}
        </div>
      ))}
    </div>
  );
}

function MoveDialog({
  device,
  overview,
  onClose,
  onMoved,
}: {
  device: HoneyroomDevice;
  overview: HoneyroomOverview;
  onClose: () => void;
  onMoved: () => Promise<void>;
}) {
  const toast = useToast();
  const [containerId, setContainerId] = useState<number | ''>('');
  const [targetSiteId, setTargetSiteId] = useState<number | ''>(device.siteId);
  const [saving, setSaving] = useState(false);

  const selectedContainer = useMemo(
    () => overview.movableContainers.find((item) => item.containerId === containerId) ?? null,
    [containerId, overview.movableContainers]
  );

  const siteOptions = useMemo(() => {
    const seen = new Set<number>();
    return overview.eligibleSites.filter((site) => {
      if (site.siteId === selectedContainer?.siteId) return false;
      if (seen.has(site.siteId)) return false;
      seen.add(site.siteId);
      return true;
    });
  }, [overview.eligibleSites, selectedContainer?.siteId]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!containerId || !targetSiteId) {
      toast.error('Välj batch och målplats');
      return;
    }
    setSaving(true);
    try {
      await honeyroomApi.moveContainer({ containerId, targetSiteId });
      toast.success('Batch flyttad');
      await onMoved();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte flytta batch');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-[24px] bg-white p-5 shadow-[0_30px_90px_rgba(15,23,42,0.25)]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">{device.name}</h2>
            <p className="text-sm text-slate-600">Flytta hel batch mellan Lager och maskinerna i tapprummet.</p>
          </div>
          <button type="button" className="btn btn-secondary px-3 py-1.5" onClick={onClose}>
            Stäng
          </button>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 p-4">
            <div className="text-sm font-semibold text-slate-900">Nuvarande innehåll</div>
            <div className="mt-2 space-y-2">
              {device.batches.length > 0 ? (
                device.batches.map((batch) => (
                  <div key={batch.containerId} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                    <div className="font-medium text-slate-900">{batch.containerCode}</div>
                    <div className="text-slate-600">{batch.productName}</div>
                    <div className="text-slate-500">{formatQty(batch.remainingKg, 'kg')} kg</div>
                  </div>
                ))
              ) : (
                <div className="rounded-xl border border-dashed border-slate-300 px-3 py-4 text-sm text-slate-500">
                  Ingen batch i maskinen.
                </div>
              )}
            </div>
          </div>

          <form onSubmit={submit} className="space-y-3 rounded-2xl border border-slate-200 p-4">
            <div className="text-sm font-semibold text-slate-900">Flytta batch</div>
            <label className="block text-sm">
              Batch
              <select className="mt-1 w-full" value={containerId} onChange={(event) => setContainerId(event.target.value ? Number(event.target.value) : '')}>
                <option value="">Välj batch</option>
                {overview.movableContainers.map((container) => (
                  <option key={container.containerId} value={container.containerId}>
                    {container.containerCode} • {container.productName} • {container.siteName} • {formatQty(container.remainingKg, 'kg')} kg
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              Till plats
              <select className="mt-1 w-full" value={targetSiteId} onChange={(event) => setTargetSiteId(event.target.value ? Number(event.target.value) : '')}>
                <option value="">Välj plats</option>
                {siteOptions.map((site) => (
                  <option key={site.siteId} value={site.siteId}>
                    {site.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="rounded-2xl bg-slate-50 px-3 py-3 text-xs text-slate-600">
              Flytten avser hela batchen. Delning av batch mellan flera tankar stöds inte i denna version.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn btn-secondary px-3 py-1.5" onClick={onClose}>
                Avbryt
              </button>
              <button type="submit" className="btn btn-success px-3 py-1.5" disabled={saving}>
                {saving ? 'Flyttar...' : 'Flytta batch'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export function HoneyroomPage() {
  const toast = useToast();
  const { canWrite } = useAuth();
  const writable = canWrite('ledger');
  const [overview, setOverview] = useState<HoneyroomOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<HoneyroomDevice | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await honeyroomApi.overview();
      setOverview(response);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte ladda tapprummet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => {
      void honeyroomApi.overview().then(setOverview).catch(() => {});
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const handleSetup = async () => {
    setSettingUp(true);
    try {
      await honeyroomApi.setupDefault();
      toast.success('Tapprummet skapades');
      await load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kunde inte skapa tapprummet');
    } finally {
      setSettingUp(false);
    }
  };

  return (
    <div className="h-full overflow-hidden bg-black p-3">
      <div className="mx-auto flex h-full min-h-0 max-w-[1366px] flex-col gap-3">
        <div className="flex h-9 shrink-0 items-center justify-end">
          {writable && overview && overview.devices.length === 0 ? (
            <button type="button" className="btn btn-success px-3 py-1.5" onClick={handleSetup} disabled={settingUp}>
              {settingUp ? 'Skapar...' : 'Skapa standardtapprum'}
            </button>
          ) : null}
        </div>

        {loading ? (
          <div className="rounded-[24px] border border-slate-200 bg-white px-5 py-8 text-sm text-slate-500">Laddar tapprum...</div>
        ) : overview && overview.devices.length === 0 ? (
          <div className="rounded-[24px] border border-dashed border-slate-300 bg-white px-5 py-10 text-sm text-slate-600">
            Inget tapprum är upplagt ännu. Skapa standardtapprum för att få maskinplatserna och dashboarden.
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-3 md:grid-cols-4 md:grid-rows-2">
            {(overview?.devices ?? []).map((device) => {
              const powered = deviceIsPowered(device);
              const batchText = device.shape === 'cabinet' ? '' : device.batches.length > 0 ? formatBatchLabel(device.batches[0].containerCode) : '';
              const productText = device.shape === 'cabinet' ? '' : device.productSummary ?? 'Tom';

              return (
                <button
                  key={device.id}
                  type="button"
                  onClick={() => setSelectedDevice(device)}
                  className="min-h-0 overflow-hidden rounded-[24px] border text-left shadow-[0_10px_30px_rgba(0,0,0,0.28)]"
                  style={{
                    fontFamily: FONT_FAMILY,
                    borderColor: CARD_BORDER,
                    background: powered ? CARD_ACTIVE : CARD_IDLE,
                  }}
                >
                  <div className="grid h-full grid-rows-[46px_1fr_54px_40px] px-4 py-3">
                    <div className="overflow-hidden">
                      <div className="truncate text-[14px] font-bold leading-5 text-white">{device.name}</div>
                      <div className="truncate text-[10px] leading-4 text-white/70">{device.model ?? device.deviceType}</div>
                    </div>

                    <div className="min-h-0 overflow-hidden">
                      <MachineVisual device={device} />
                    </div>

                    <div className="flex min-h-0 flex-col items-center justify-end overflow-hidden">
                      {device.shape === 'cabinet' ? null : (
                        <>
                          <div
                            className="max-w-full truncate text-center leading-none text-white"
                            style={{ fontSize: '3rem', fontWeight: 400 }}
                          >
                            {batchText}
                          </div>
                          <div
                            className="mt-1 max-w-full truncate text-center leading-tight text-white"
                            style={{ fontSize: '0.95rem', fontWeight: 700 }}
                          >
                            {productText}
                          </div>
                        </>
                      )}
                    </div>

                    <MetricRow device={device} />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selectedDevice && overview ? (
        <MoveDialog device={selectedDevice} overview={overview} onClose={() => setSelectedDevice(null)} onMoved={load} />
      ) : null}
    </div>
  );
}
